import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Store, ShoppingCart, Palette, CheckCircle } from 'lucide-react';

const contactInfo = [
  {
    icon: Phone,
    title: '联系电话',
    content: '17378287432',
    description: '7×24小时为您服务'
  },
  {
    icon: Mail,
    title: '电子邮箱',
    content: '283707357@qq.com',
    description: '工作日24小时内回复'
  },
  {
    icon: MapPin,
    title: '生产地址',
    content: '湖南省临湘市三湾工业园标准化厂房5栋',
    description: '欢迎莅临参观'
  },
  {
    icon: Clock,
    title: '工作时间',
    content: '周一至周六 8:30-17:30',
    description: '节假日正常值班'
  }
];

const cooperationModes = [
  {
    id: 'store',
    icon: Store,
    title: '钓具店合作',
    subtitle: '线下渠道合作',
    description: '与全国各地钓具店建立长期稳定的合作关系，提供优质产品供应、营销支持和售后服务。',
    benefits: [
      '灵活订货政策',
      '陈列支持',
      '促销物料支持',
      '售后服务保障'
    ],
    color: 'from-deep-lake to-deep-lake-light',
    bgColor: 'bg-deep-lake/5'
  },
  {
    id: 'ecommerce',
    icon: ShoppingCart,
    title: '电商渠道',
    subtitle: '线上平台合作',
    description: '诚邀各大电商平台、直播带货主播、社群团购等线上渠道合作伙伴，共同开拓线上市场。',
    benefits: [
      '一件代发服务',
      '专属链接支持',
      '营销素材提供',
      '售后无忧保障'
    ],
    color: 'from-dawn-orange to-[#FF9F43]',
    bgColor: 'bg-dawn-orange/5'
  },
  {
    id: 'custom',
    icon: Palette,
    title: '定制合作',
    subtitle: '个性化定制服务',
    description: '为企业客户、钓鱼俱乐部、赛事活动等提供专属定制浮漂服务，打造独一无二的品牌浮漂。',
    benefits: [
      '专业设计团队',
      '灵活定制方案',
      '保密协议',
      '品质保证承诺'
    ],
    color: 'from-warm-gold to-[#D4AF37]',
    bgColor: 'bg-warm-gold/5'
  }
];

export default function Contact() {
  const [activeMode, setActiveMode] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleModeClick = (modeId: string) => {
    setActiveMode(modeId);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <main className="pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-deep-lake to-deep-lake-dark">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif-zh font-bold text-white mb-6">
            商务合作
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto">
            携手民间艺人，共创钓鱼产业新未来
          </p>
        </div>
      </section>

      {/* Cooperation Modes Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4">
              Cooperation Mode
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif-zh font-bold text-deep-lake mb-6">
              加盟合作模式
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              根据您的业务需求，选择最适合的合作方式，我们期待与您建立长期共赢的合作关系
            </p>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
              <div className="bg-green-500 text-white px-8 py-4 rounded-xl shadow-2xl flex items-center gap-3">
                <CheckCircle className="w-6 h-6" />
                <span className="font-medium">感谢您的关注！我们的商务团队将尽快与您联系</span>
              </div>
            </div>
          )}

          {/* Cooperation Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {cooperationModes.map((mode) => (
              <div
                key={mode.id}
                className={`group relative rounded-2xl overflow-hidden border-2 transition-all duration-500 cursor-pointer ${
                  activeMode === mode.id
                    ? 'border-deep-lake shadow-2xl scale-105'
                    : 'border-gray-100 hover:border-deep-lake/30 hover:shadow-xl'
                }`}
                onClick={() => handleModeClick(mode.id)}
              >
                {/* Card Header */}
                <div className={`h-32 bg-gradient-to-br ${mode.color} flex items-center justify-center relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <mode.icon className="w-16 h-16 text-white relative z-10" />
                  <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white/10 rounded-full" />
                  <div className="absolute -top-8 -left-8 w-24 h-24 bg-white/10 rounded-full" />
                </div>

                {/* Card Content */}
                <div className={`p-8 ${mode.bgColor}`}>
                  <span className="text-sm text-gray-500 font-medium">{mode.subtitle}</span>
                  <h3 className="text-2xl font-serif-zh font-bold text-gray-900 mt-2 mb-4">
                    {mode.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {mode.description}
                  </p>

                  {/* Benefits */}
                  <div className="space-y-3">
                    {mode.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${mode.color} flex items-center justify-center flex-shrink-0`}>
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button - 已取消 */}
                  {/*
                  <button
                    className={`w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all duration-300 ${
                      activeMode === mode.id
                        ? 'bg-deep-lake text-white'
                        : 'bg-white text-deep-lake border-2 border-deep-lake hover:bg-deep-lake hover:text-white'
                    }`}
                  >
                    了解详情
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  */}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA - 已取消 */}
          {/*
          <div className="bg-gradient-to-r from-deep-lake/5 via-warm-gold/5 to-dawn-orange/5 rounded-3xl p-8 md:p-12 text-center">
            <h3 className="text-2xl md:text-3xl font-serif-zh font-bold text-gray-900 mb-4">
              有合作意向？
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              无论您是钓具店主、电商从业者，还是有定制需求的企业客户，我们都期待与您深入沟通，探讨合作可能。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:400-888-8888"
                className="inline-flex items-center gap-2 px-8 py-4 bg-deep-lake text-white rounded-xl font-medium hover:bg-deep-lake-light transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                立即拨打热线
              </a>
              <a
                href="mailto:business@folkartist.com"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-deep-lake border-2 border-deep-lake rounded-xl font-medium hover:bg-deep-lake hover:text-white transition-all duration-300"
              >
                <Mail className="w-5 h-5" />
                发送商务邮件
              </a>
            </div>
          </div>
          */}
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4">
              Contact Information
            </span>
            <h2 className="text-3xl md:text-4xl font-serif-zh font-bold text-deep-lake mb-6">
              联系我们
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              期待与您的每一次交流，用心服务每一位合作伙伴
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((item, index) => (
              <div
                key={index}
                className="p-6 bg-white rounded-2xl hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-deep-lake to-deep-lake-light flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-serif-zh font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-deep-lake font-medium mb-1">{item.content}</p>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Map Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="aspect-[16/9] rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 shadow-lg">
                <img
                  src="/images/map.png?v=1"
                  alt="交通地图"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg">
              <h3 className="font-serif-zh font-bold text-xl text-gray-900 mb-6">交通指南</h3>
              <div className="flex items-start gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-deep-lake/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-deep-lake" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">生产地址</p>
                  <p className="text-sm text-gray-500 mt-1">湖南省临湘市三湾工业园标准化厂房5栋</p>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  欢迎莅临民间艺人参观指导，我们的工厂位于湖南省临湘市三湾工业园标准化厂房5栋。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}